import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface WeekDateNavigationProps {
  currentDate: DateTime;
  onDateChange: (date: DateTime) => void;
  onPrevious: () => void;
  onNext: () => void;
  className?: string;
}

export function WeekDateNavigation({
  currentDate,
  onDateChange,
  onPrevious,
  onNext,
  className,
}: WeekDateNavigationProps) {
  // Calculate week start and end
  const weekStart = currentDate.startOf("week");
  const weekEnd = currentDate.endOf("week");

  // Format week range display
  const formatWeekRange = () => {
    if (weekStart.month === weekEnd.month) {
      return `${weekStart.toFormat("MMM d")}-${weekEnd.toFormat("d, yyyy")}`;
    }
    if (weekStart.year === weekEnd.year) {
      return `${weekStart.toFormat("MMM d")} - ${weekEnd.toFormat("MMM d, yyyy")}`;
    }
    return `${weekStart.toFormat("MMM d, yyyy")} - ${weekEnd.toFormat("MMM d, yyyy")}`;
  };

  // Quick week navigation options
  const quickWeekOptions = [
    { label: "This Week", date: DateTime.now().startOf("week") },
    { label: "Next Week", date: DateTime.now().plus({ weeks: 1 }).startOf("week") },
    { label: "Previous Week", date: DateTime.now().minus({ weeks: 1 }).startOf("week") },
  ];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Previous Week Button */}
      <Button variant="ghost" size="icon" onClick={onPrevious} className="h-8 w-8">
        <ChevronLeft className="h-4 w-4" />
      </Button>

      {/* Week Range Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="flex items-center gap-2 h-8 px-3">
            <Calendar className="h-3.5 w-3.5" />
            <span className="text-sm font-medium">{formatWeekRange()}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {quickWeekOptions.map((option) => (
            <DropdownMenuItem
              key={option.label}
              onClick={() => onDateChange(option.date)}
              className={cn(
                "cursor-pointer",
                weekStart.hasSame(option.date, "week") && "bg-accent"
              )}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Next Week Button */}
      <Button variant="ghost" size="icon" onClick={onNext} className="h-8 w-8">
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
